import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Clock, AlertCircle, Lightbulb, Building,
  Search, Filter, Eye, RefreshCw, Loader2, Calendar, User, Tag,
  ChevronRight, Award, Star, Globe, FileText, ArrowLeft
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

// PracticeCard component - moved outside ApprovalHub
const PracticeCard = ({ practice, processingId, onViewDetails, onReject, onApprove }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{practice.title}</h3>
        <p className="text-sm text-gray-600 line-clamp-2">{practice.description}</p>
      </div>
      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center">
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </span>
    </div>
    
    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
      <span className="flex items-center">
        <Building className="h-4 w-4 mr-1" />
        {practice.organization_name || 'Unknown Org'}
      </span>
      <span className="flex items-center">
        <Tag className="h-4 w-4 mr-1" />
        {practice.pillar || 'N/A'}
      </span>
      <span className="flex items-center">
        <Calendar className="h-4 w-4 mr-1" />
        {new Date(practice.submitted_at || practice.created_at).toLocaleDateString()}
      </span>
    </div>
    
    {practice.tags && practice.tags.length > 0 && (
      <div className="flex flex-wrap gap-1 mb-4">
        {practice.tags.slice(0, 4).map((tag, index) => (
          <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
            {tag}
          </span>
        ))}
      </div>
    )}
    
    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
      <button 
        onClick={() => onViewDetails(practice)}
        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
      >
        <Eye className="h-4 w-4 mr-1" />
        View Details
      </button>
      
      <div className="flex items-center space-x-2">
        <button 
          onClick={() => onReject(practice.id)}
          disabled={processingId === practice.id}
          className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50"
        >
          {processingId === practice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 inline mr-1" />}
          Reject
        </button>
        <button 
          onClick={() => onApprove(practice.id)}
          disabled={processingId === practice.id}
          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
        >
          {processingId === practice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 inline mr-1" />}
          Approve
        </button>
      </div>
    </div>
  </div>
);

// ProfileCard component - moved outside ApprovalHub
const ProfileCard = ({ profile, processingId, onViewDetails, onReject, onApprove }) => {
  const getRoleBadge = (role) => {
    const badges = {
      org_admin: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Org Admin' },
      evaluator: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Evaluator' },
      admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Admin' }
    };
    const badge = badges[role] || { bg: 'bg-gray-100', text: 'text-gray-700', label: role };
    return <span className={`px-2 py-0.5 ${badge.bg} ${badge.text} rounded text-xs`}>{badge.label}</span>;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Inactive' }
    };
    const badge = badges[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
    return <span className={`px-2 py-0.5 ${badge.bg} ${badge.text} rounded text-xs`}>{badge.label}</span>;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            {profile.logo_url ? (
              <img src={profile.logo_url} alt={profile.name} className="w-full h-full object-cover rounded-lg" />
            ) : (
              <Building className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{profile.public_name || profile.name}</h3>
            <p className="text-sm text-blue-600">{profile.tagline || 'No tagline'}</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium flex items-center">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>
      </div>
      
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{profile.description || 'No description provided'}</p>
      
      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
        <span className="flex items-center">
          <Globe className="h-4 w-4 mr-1" />
          {profile.industry || 'N/A'}
        </span>
        <span className="flex items-center">
          <Award className="h-4 w-4 mr-1" />
          {profile.tier || 'N/A'}
        </span>
        <span className="flex items-center">
          <User className="h-4 w-4 mr-1" />
          {profile.size || 'N/A'}
        </span>
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <button 
          onClick={() => onViewDetails(profile)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
        >
          <Eye className="h-4 w-4 mr-1" />
          View Details
        </button>
        
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => onReject(profile.id)}
            disabled={processingId === profile.id}
            className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50"
          >
            {processingId === profile.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 inline mr-1" />}
            Reject
          </button>
          <button 
            onClick={() => onApprove(profile.id)}
            disabled={processingId === profile.id}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
          >
            {processingId === profile.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 inline mr-1" />}
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

// DetailModal component - moved outside ApprovalHub
const DetailModal = ({ selectedItem, onClose, onReject, onApprove }) => {
  if (!selectedItem) return null;
  
  const { type, data } = selectedItem;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">
                {type === 'practice' ? data.title : (data.public_name || data.name)}
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                {type === 'practice' ? 'Best Practice Submission' : 'Directory Profile Submission'}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-blue-200"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {type === 'practice' ? (
            <>
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                <p className="text-gray-900">{data.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Organization</h3>
                  <p className="text-gray-900">{data.organization_name}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Pillar</h3>
                  <p className="text-gray-900">{data.pillar}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Industry</h3>
                  <p className="text-gray-900">{data.industry || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Submitted</h3>
                  <p className="text-gray-900">{new Date(data.submitted_at || data.created_at).toLocaleString()}</p>
                </div>
              </div>
              
              {data.key_outcomes && data.key_outcomes.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Key Outcomes</h3>
                  <ul className="space-y-1">
                    {data.key_outcomes.map((outcome, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {data.implementation && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Implementation Details</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Timeframe</p>
                      <p className="text-sm font-medium">{data.implementation.timeframe || 'N/A'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Investment</p>
                      <p className="text-sm font-medium">{data.implementation.investment || 'N/A'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Difficulty</p>
                      <p className="text-sm font-medium">{data.implementation.difficulty || 'N/A'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">ROI</p>
                      <p className="text-sm font-medium">{data.implementation.roi || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                <p className="text-gray-900">{data.description || 'No description provided'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Tagline</h3>
                  <p className="text-gray-900">{data.tagline || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Industry</h3>
                  <p className="text-gray-900">{data.industry || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Location</h3>
                  <p className="text-gray-900">{data.location || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Size</h3>
                  <p className="text-gray-900">{data.size || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Tier</h3>
                  <p className="text-gray-900">{data.tier || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Founded</h3>
                  <p className="text-gray-900">{data.founded || 'N/A'}</p>
                </div>
              </div>
              
              {data.specializations && data.specializations.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Specializations</h3>
                  <div className="flex flex-wrap gap-2">
                    {data.specializations.map((spec, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button 
              onClick={() => {
                onReject(data.id, type);
                onClose();
              }}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 inline mr-1" />
              Reject
            </button>
            <button 
              onClick={() => {
                onApprove(data.id, type);
                onClose();
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 inline mr-1" />
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ApprovalHub = () => {
  const [activeTab, setActiveTab] = useState('best-practices');
  const [pendingPractices, setPendingPractices] = useState([]);
  const [pendingProfiles, setPendingProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [actionStatus, setActionStatus] = useState({ type: '', message: '' });
  const [stats, setStats] = useState({ practices: 0, profiles: 0 });
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch pending best practices
  const fetchPendingPractices = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/best-practices/admin/pending`);
      if (response.ok) {
        const data = await response.json();
        setPendingPractices(data.practices || []);
        return data.practices?.length || 0;
      }
      return 0;
    } catch (err) {
      console.error('Error fetching pending practices:', err);
      return 0;
    }
  }, [BACKEND_URL]);

  // Fetch pending directory profiles
  const fetchPendingProfiles = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/directory/admin/pending`);
      if (response.ok) {
        const data = await response.json();
        setPendingProfiles(data.profiles || []);
        return data.profiles?.length || 0;
      }
      return 0;
    } catch (err) {
      console.error('Error fetching pending profiles:', err);
      return 0;
    }
  }, [BACKEND_URL]);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [practicesCount, profilesCount] = await Promise.all([
        fetchPendingPractices(),
        fetchPendingProfiles()
      ]);
      setStats({ practices: practicesCount, profiles: profilesCount });
    } catch (err) {
      setError('Failed to load pending items');
    } finally {
      setLoading(false);
    }
  }, [fetchPendingPractices, fetchPendingProfiles]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Approve a best practice
  const approvePractice = async (practiceId) => {
    setProcessingId(practiceId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/best-practices/${practiceId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        setActionStatus({ type: 'success', message: 'Practice approved successfully!' });
        await loadData();
        setTimeout(() => setActionStatus({ type: '', message: '' }), 3000);
      } else {
        throw new Error('Failed to approve');
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: 'Failed to approve practice' });
    } finally {
      setProcessingId(null);
    }
  };

  // Reject a best practice
  const rejectPractice = async (practiceId, reason = '') => {
    setProcessingId(practiceId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/best-practices/${practiceId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        setActionStatus({ type: 'success', message: 'Practice rejected' });
        await loadData();
        setTimeout(() => setActionStatus({ type: '', message: '' }), 3000);
      } else {
        throw new Error('Failed to reject');
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: 'Failed to reject practice' });
    } finally {
      setProcessingId(null);
    }
  };

  // Approve a directory profile
  const approveProfile = async (profileId) => {
    setProcessingId(profileId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/directory/${profileId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        setActionStatus({ type: 'success', message: 'Profile approved successfully!' });
        await loadData();
        setTimeout(() => setActionStatus({ type: '', message: '' }), 3000);
      } else {
        throw new Error('Failed to approve');
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: 'Failed to approve profile' });
    } finally {
      setProcessingId(null);
    }
  };

  // Reject a directory profile
  const rejectProfile = async (profileId, reason = '') => {
    setProcessingId(profileId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/directory/${profileId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        setActionStatus({ type: 'success', message: 'Profile rejected' });
        await loadData();
        setTimeout(() => setActionStatus({ type: '', message: '' }), 3000);
      } else {
        throw new Error('Failed to reject');
      }
    } catch (err) {
      setActionStatus({ type: 'error', message: 'Failed to reject profile' });
    } finally {
      setProcessingId(null);
    }
  };

  // Handle view details
  const handleViewPracticeDetails = (practice) => {
    setSelectedItem({ type: 'practice', data: practice });
    setShowDetailModal(true);
  };

  const handleViewProfileDetails = (profile) => {
    setSelectedItem({ type: 'profile', data: profile });
    setShowDetailModal(true);
  };

  // Handle modal actions
  const handleModalReject = (id, type) => {
    if (type === 'practice') {
      rejectPractice(id);
    } else {
      rejectProfile(id);
    }
  };

  const handleModalApprove = (id, type) => {
    if (type === 'practice') {
      approvePractice(id);
    } else {
      approveProfile(id);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedItem(null);
  };

  return (
    <SystemAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Approval Hub</h1>
          <p className="text-gray-600 mt-1">Review and approve pending submissions</p>
        </div>

        {/* Status Alert */}
        {actionStatus.message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
            actionStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {actionStatus.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span>{actionStatus.message}</span>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Best Practices</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.practices}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Lightbulb className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Directory Profiles</p>
              <p className="text-3xl font-bold text-blue-600">{stats.profiles}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Pending</p>
              <p className="text-3xl font-bold text-gray-900">{stats.practices + stats.profiles}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg">
              <Clock className="h-6 w-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('best-practices')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'best-practices'
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Lightbulb className="h-4 w-4 inline mr-2" />
            Best Practices ({stats.practices})
          </button>
          <button
            onClick={() => setActiveTab('directory')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'directory'
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Building className="h-4 w-4 inline mr-2" />
            Directory Profiles ({stats.profiles})
          </button>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end mb-4">
        <button 
          onClick={loadData}
          disabled={loading}
          className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : error ? (
        <div className="text-center py-12 bg-red-50 rounded-lg">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button onClick={loadData} className="mt-4 text-blue-600 hover:underline">
            Try Again
          </button>
        </div>
      ) : (
        <>
          {activeTab === 'best-practices' && (
            pendingPractices.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {pendingPractices.map((practice) => (
                  <PracticeCard 
                    key={practice.id} 
                    practice={practice}
                    processingId={processingId}
                    onViewDetails={handleViewPracticeDetails}
                    onReject={rejectPractice}
                    onApprove={approvePractice}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">All caught up!</p>
                <p className="text-gray-500 text-sm mt-1">No pending best practices to review</p>
              </div>
            )
          )}
          
          {activeTab === 'directory' && (
            pendingProfiles.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {pendingProfiles.map((profile) => (
                  <ProfileCard 
                    key={profile.id} 
                    profile={profile}
                    processingId={processingId}
                    onViewDetails={handleViewProfileDetails}
                    onReject={rejectProfile}
                    onApprove={approveProfile}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">All caught up!</p>
                <p className="text-gray-500 text-sm mt-1">No pending directory profiles to review</p>
              </div>
            )
          )}
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <DetailModal 
          selectedItem={selectedItem}
          onClose={handleCloseModal}
          onReject={handleModalReject}
          onApprove={handleModalApprove}
        />
      )}
      </div>
    </SystemAdminLayout>
  );
};

export default ApprovalHub;
